import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, LogOut, Eye, Users, MousePointerClick, Sparkles, Mail, RefreshCw, MailCheck, MailX, Download } from 'lucide-react';
import { toast } from 'sonner';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'mt_blog_admin_token';
const RANGES = [7, 30, 90];

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="p-5 rounded-2xl bg-[#1e2a4a]/50 border border-[#d4c4a8]/15" data-testid={`analytics-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-[#e89b47]" />
      <p className="font-display text-xs tracking-widest text-[#d4c4a8]/60 uppercase">{label}</p>
    </div>
    <p className="font-display text-3xl text-[#e8dcc8]">{value}</p>
  </div>
);

const ListCard = ({ title, rows, keyField, valueField, testId }) => (
  <div className="p-5 rounded-2xl bg-[#1e2a4a]/50 border border-[#d4c4a8]/15" data-testid={testId}>
    <h3 className="font-display text-sm tracking-widest text-[#e89b47] uppercase mb-4">{title}</h3>
    {rows.length === 0 ? (
      <p className="font-body text-sm text-[#d4c4a8]/50">No data yet</p>
    ) : (
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between gap-3 text-sm">
            <span className="font-body text-[#e8dcc8]/90 truncate">{r[keyField] || '/'}</span>
            <span className="font-display text-[#e89b47] shrink-0">{r[valueField]}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const AnalyticsAdminPage = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [tokenInput, setTokenInput] = useState('');
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subs, setSubs] = useState(null);

  const fetchSubscribers = async (tok) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/reminders/subscribers`, {
        headers: { 'X-Admin-Token': tok },
      });
      if (res.ok) setSubs(await res.json());
    } catch { /* subscriber section simply hides */ }
  };

  const exportSubscribers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/reminders/subscribers.csv`, {
        headers: { 'X-Admin-Token': token },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `marketing-tarot-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Subscriber list downloaded');
    } catch {
      toast.error('Export failed, please try again');
    }
  };

  useEffect(() => {
    const verify = async () => {
      if (!token) return;
      setChecking(true);
      try {
        const res = await fetch(`${API_URL}/api/admin/blog/verify`, {
          method: 'POST',
          headers: { 'X-Admin-Token': token, 'Content-Type': 'application/json' },
          body: '{}',
        });
        if (res.ok) setAuthed(true);
        else { localStorage.removeItem(TOKEN_KEY); setToken(''); }
      } catch { setAuthed(false); }
      finally { setChecking(false); }
    };
    verify();
  }, [token]);

  const fetchSummary = async (range = days) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/analytics/summary?days=${range}`, {
        headers: { 'X-Admin-Token': token },
      });
      if (!res.ok) throw new Error('Failed to load analytics');
      setData(await res.json());
    } catch {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) {
      fetchSummary(days);
      fetchSubscribers(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, days]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    setChecking(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/blog/verify`, {
        method: 'POST',
        headers: { 'X-Admin-Token': tokenInput.trim(), 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (res.ok) {
        localStorage.setItem(TOKEN_KEY, tokenInput.trim());
        setToken(tokenInput.trim());
        setAuthed(true);
      } else toast.error('Invalid token');
    } catch { toast.error('Verification failed'); }
    finally { setChecking(false); }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0f1f35] to-[#0a1628] text-[#e8dcc8] flex items-center justify-center px-6">
        <div className="w-full max-w-md p-8 rounded-2xl bg-[#1e2a4a]/50 border border-[#d4c4a8]/15">
          <h1 className="font-display text-2xl text-[#e8dcc8] mb-2 text-center">Analytics</h1>
          <p className="font-body text-sm text-[#d4c4a8]/60 mb-6 text-center">Enter your admin token to view site analytics.</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              placeholder="Admin token"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-[#0a1628] border border-[#d4c4a8]/20 text-[#e8dcc8] focus:outline-none focus:border-[#e89b47] font-body text-sm"
              data-testid="analytics-token-input"
              autoFocus
            />
            <button
              type="submit"
              disabled={checking || !tokenInput.trim()}
              className="w-full h-11 rounded-xl bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] font-display tracking-widest text-sm disabled:opacity-50 hover:scale-[1.02] transition-transform"
              data-testid="analytics-signin-btn"
            >
              {checking ? 'Verifying…' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const totals = data?.totals || {};
  const eventMap = Object.fromEntries((data?.events || []).map((e) => [e.event_type, e.count]));

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0f1f35] to-[#0a1628] text-[#e8dcc8]">
      <div className="max-w-6xl mx-auto px-6 pt-6 flex items-center justify-between gap-3">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-[#d4c4a8]/60 hover:text-[#e89b47] transition-colors font-body text-sm"
          data-testid="analytics-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Home
        </button>
        <button
          onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(''); setAuthed(false); }}
          className="inline-flex items-center gap-2 text-[#d4c4a8]/60 hover:text-[#e89b47] transition-colors font-body text-sm"
          data-testid="analytics-logout-btn"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <h1 className="font-display text-3xl text-[#e8dcc8]">Site Analytics</h1>
          <div className="flex items-center gap-2">
            {RANGES.map((r) => (
              <button
                key={r}
                onClick={() => setDays(r)}
                className={`px-4 h-9 rounded-lg text-xs font-display tracking-widest border transition-colors ${
                  days === r
                    ? 'bg-[#e89b47] text-[#0a1628] border-[#e89b47]'
                    : 'text-[#d4c4a8] border-[#d4c4a8]/20 hover:border-[#e89b47]'
                }`}
                data-testid={`analytics-range-${r}`}
              >
                {r} days
              </button>
            ))}
            <button
              onClick={() => fetchSummary(days)}
              className="px-3 h-9 rounded-lg text-[#d4c4a8]/60 border border-[#d4c4a8]/20 hover:border-[#e89b47] hover:text-[#e89b47] transition-colors"
              data-testid="analytics-refresh-btn"
              title="Refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {loading && !data && (
          <div className="flex items-center gap-2 text-[#d4c4a8]/60 font-body text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="analytics-stats-grid">
              <StatCard icon={Eye} label="Page Views" value={totals.page_views ?? 0} />
              <StatCard icon={Users} label="Unique Visitors" value={totals.unique_visitors ?? 0} />
              <StatCard icon={MousePointerClick} label="Sessions" value={totals.sessions ?? 0} />
              <StatCard icon={Sparkles} label="Readings" value={eventMap.reading_generated ?? totals.readings ?? 0} />
              <StatCard icon={Mail} label="Contact Leads" value={eventMap.contact_submitted ?? totals.contact_leads ?? 0} />
            </div>

            <div className="p-5 rounded-2xl bg-[#1e2a4a]/50 border border-[#d4c4a8]/15 mb-8" data-testid="analytics-daily-chart">
              <h3 className="font-display text-sm tracking-widest text-[#e89b47] uppercase mb-4">Daily Page Views</h3>
              {(data.daily || []).length === 0 ? (
                <p className="font-body text-sm text-[#d4c4a8]/50 py-8 text-center">
                  No traffic recorded yet — data starts collecting from today.
                </p>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.daily} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                      <defs>
                        <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#e89b47" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="#e89b47" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#d4c4a8" strokeOpacity={0.08} vertical={false} />
                      <XAxis dataKey="date" tick={{ fill: '#d4c4a8', fontSize: 11, opacity: 0.6 }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fill: '#d4c4a8', fontSize: 11, opacity: 0.6 }} tickLine={false} axisLine={false} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ background: '#1e2a4a', border: '1px solid rgba(212,196,168,0.3)', borderRadius: 8, color: '#e8dcc8' }}
                        labelStyle={{ color: '#e89b47' }}
                      />
                      <Area type="monotone" dataKey="views" stroke="#e89b47" strokeWidth={2} fill="url(#viewsGrad)" name="Views" />
                      <Area type="monotone" dataKey="visitors" stroke="#d4c4a8" strokeWidth={1.5} fillOpacity={0} name="Visitors" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <ListCard title="Top Pages" rows={data.top_pages || []} keyField="path" valueField="views" testId="analytics-top-pages" />
              <ListCard title="Traffic Sources" rows={data.referrers || []} keyField="source" valueField="views" testId="analytics-referrers" />
              <ListCard
                title="Campaigns (UTM)"
                rows={(data.campaigns || []).map((c) => ({
                  label: `${c.source}${c.campaign ? ' · ' + c.campaign : c.medium ? ' · ' + c.medium : ''}`,
                  views: c.views,
                }))}
                keyField="label"
                valueField="views"
                testId="analytics-campaigns"
              />
              <ListCard title="Devices" rows={(data.devices || []).filter((d) => d.device !== 'server')} keyField="device" valueField="views" testId="analytics-devices" />
            </div>

            <div className="p-5 rounded-2xl bg-[#1e2a4a]/50 border border-[#d4c4a8]/15" data-testid="analytics-funnel">
              <h3 className="font-display text-sm tracking-widest text-[#e89b47] uppercase mb-4">Conversion Events (last {data.days} days)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { label: 'Tarot Readings', key: 'reading_generated' },
                  { label: '90-Day Plans', key: 'plan_generated' },
                  { label: 'Contact Leads', key: 'contact_submitted' },
                ].map((f) => (
                  <div key={f.key} className="text-center p-4 rounded-xl bg-[#0a1628]/60 border border-[#d4c4a8]/10">
                    <p className="font-display text-2xl text-[#e8dcc8]">{eventMap[f.key] ?? 0}</p>
                    <p className="font-body text-xs text-[#d4c4a8]/60 mt-1">{f.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Reminder subscribers */}
            <div className="p-5 rounded-2xl bg-[#1e2a4a]/50 border border-[#d4c4a8]/15 mt-8" data-testid="analytics-subscribers">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="font-display text-sm tracking-widest text-[#e89b47] uppercase">Monthly Reminder Subscribers</h3>
                {subs && (
                  <div className="flex items-center gap-4 font-body text-xs text-[#d4c4a8]/60">
                    <span className="inline-flex items-center gap-1.5" data-testid="subs-active-count">
                      <MailCheck className="w-3.5 h-3.5 text-[#e89b47]" /> {subs.active} active
                    </span>
                    <span className="inline-flex items-center gap-1.5" data-testid="subs-unsub-count">
                      <MailX className="w-3.5 h-3.5 text-[#d4c4a8]/40" /> {subs.unsubscribed} unsubscribed
                    </span>
                    {subs.total > 0 && (
                      <button
                        type="button"
                        onClick={exportSubscribers}
                        className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg font-display text-[10px] tracking-widest uppercase text-[#e89b47] border border-[#e89b47]/30 hover:bg-[#e89b47]/10 transition-colors"
                        data-testid="subs-export-btn"
                      >
                        <Download className="w-3.5 h-3.5" /> Export CSV
                      </button>
                    )}
                  </div>
                )}
              </div>
              {!subs || subs.subscribers.length === 0 ? (
                <p className="font-body text-sm text-[#d4c4a8]/50 py-6 text-center" data-testid="subs-empty">
                  No subscribers yet — signups from the reading form and footer will appear here.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left" data-testid="subs-table">
                    <thead>
                      <tr className="border-b border-[#d4c4a8]/10">
                        <th className="py-2 pr-4 font-display text-[10px] tracking-widest text-[#d4c4a8]/50 uppercase">Email</th>
                        <th className="py-2 pr-4 font-display text-[10px] tracking-widest text-[#d4c4a8]/50 uppercase">Signed up</th>
                        <th className="py-2 font-display text-[10px] tracking-widest text-[#d4c4a8]/50 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subs.subscribers.map((s) => (
                        <tr key={s.email} className="border-b border-[#d4c4a8]/5">
                          <td className="py-2.5 pr-4 font-body text-sm text-[#e8dcc8]">{s.email}</td>
                          <td className="py-2.5 pr-4 font-body text-xs text-[#d4c4a8]/60">
                            {s.created_at ? new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                          </td>
                          <td className="py-2.5">
                            {s.active !== false ? (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-display tracking-wider uppercase text-[#e89b47] border border-[#e89b47]/30 bg-[#e89b47]/10">Active</span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-display tracking-wider uppercase text-[#d4c4a8]/40 border border-[#d4c4a8]/15">Unsubscribed</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsAdminPage;
